import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trackFormSubmission } from '@/utils/analytics';
import { Share2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GroupAccommodationForm({ eventDetails = {} }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    phone: '',
    groupSize: '',
    dates: '',
    budget: '',
    notes: eventDetails.defaultNotes || '',
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ type: '', message: '' });

    try {
      const payload = {
        ...formData,
        ...eventDetails.hiddenFields,
      };

      const response = await axios.post(`${API}/forms/events/group-enquiry`, payload);
      
      // Track form submission
      trackFormSubmission('group_enquiry', {
        event_name: eventDetails.hiddenFields?.event_name,
        group_size: formData.groupSize,
      });
      
      setStatus({
        type: 'success',
        message: t('form.success'),
      });
      // Reset form
      setFormData({
        companyName: '',
        contactName: '',
        contactEmail: '',
        phone: '',
        groupSize: '',
        dates: '',
        budget: '',
        notes: eventDetails.defaultNotes || '',
      });
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        type: 'error',
        message: t('form.error'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg border border-gray-200">
      <div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">{t('form.title')}</h3>
        <p className="text-sm text-gray-600">{t('form.subtitle')}</p>
      </div>

      {/* Company Name */}
      <div>
        <Label htmlFor="companyName" className="text-sm font-medium text-gray-700 mb-2">
          {t('form.companyName')} <span className="text-red-600">*</span>
        </Label>
        <Input
          id="companyName"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          required
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          data-testid="company-name-input"
        />
      </div>

      {/* Contact Name and Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contactName" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.contactName')} <span className="text-red-600">*</span>
          </Label>
          <Input
            id="contactName"
            name="contactName"
            value={formData.contactName}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            data-testid="contact-name-input"
          />
        </div>

        <div>
          <Label htmlFor="contactEmail" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.email')} <span className="text-red-600">*</span>
          </Label>
          <Input
            type="email"
            id="contactEmail"
            name="contactEmail"
            value={formData.contactEmail}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            data-testid="contact-email-input"
          />
        </div>
      </div>

      {/* Phone and Group Size */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.phone')} <span className="text-red-600">*</span>
          </Label>
          <Input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            data-testid="phone-input"
          />
        </div>

        <div>
          <Label htmlFor="groupSize" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.groupSize')} <span className="text-red-600">*</span>
          </Label>
          <Input
            type="number"
            id="groupSize"
            name="groupSize"
            value={formData.groupSize}
            onChange={handleChange}
            required
            min="1"
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder={t('form.groupSizePlaceholder')}
            data-testid="group-size-input"
          />
        </div>
      </div>

      {/* Dates and Budget */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="dates" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.eventDates')} <span className="text-red-600">*</span>
          </Label>
          <Input
            id="dates"
            name="dates"
            value={formData.dates}
            onChange={handleChange}
            required
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder={t('form.datesPlaceholder')}
            data-testid="dates-input"
          />
        </div>

        <div>
          <Label htmlFor="budget" className="text-sm font-medium text-gray-700 mb-2">
            {t('form.budget')}
          </Label>
          <Input
            id="budget"
            name="budget"
            value={formData.budget}
            onChange={handleChange}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
            placeholder={t('form.budgetPlaceholder')}
            data-testid="budget-input"
          />
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <Label htmlFor="notes" className="text-sm font-medium text-gray-700 mb-2">
          {t('form.additionalReq')}
        </Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-orange-500"
          placeholder={t('form.notesPlaceholder')}
          data-testid="notes-input"
        />
      </div>

      {/* Status Messages */}
      {status.message && (
        <div
          className={`p-4 rounded-lg ${
            status.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
          data-testid="form-status-message"
        >
          {status.message}
          
          {/* Share prompt after successful submission */}
          {status.type === 'success' && eventDetails?.hiddenFields?.event_name && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <div className="flex items-center gap-2 text-green-700">
                <Share2 className="w-4 h-4" />
                <span className="text-sm font-medium">{t('form.knowColleagues')}</span>
              </div>
              <p className="text-sm text-green-600 mt-1 mb-3">
                {t('form.sharePrompt')}
              </p>
              <a 
                href="/boost/dashboard"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm font-medium hover:from-orange-600 hover:to-amber-600 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {t('form.shareEarn')}
              </a>
            </div>
          )}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={loading}
        className="w-full bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-medium"
        data-testid="submit-enquiry-button"
      >
        {loading ? t('form.submitting') : t('form.getQuote')}
      </Button>

      <p className="text-xs text-gray-500 text-center">
        {t('form.disclaimer')}
      </p>
    </form>
  );
}